import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';

type ProviderRow = { id: number; name: string; type?: string | null; status?: string | null };

const SelectProviderPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<ProviderRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiService.listProvidersForSuperAdmin({
          page: 1,
          limit: 500,
          status: 'active',
        });
        if (!cancelled) {
          setProviders(res.data.providers ?? []);
        }
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } } };
        if (!cancelled) {
          setError(err.response?.data?.error || 'Impossible de charger les partenaires.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter(
      (p) => p.name.toLowerCase().includes(q) || String(p.id).includes(q)
    );
  }, [providers, search]);

  const handleChoose = (id: number) => {
    apiService.setProviderDashboardContextId(id);
    void queryClient.invalidateQueries();
    navigate('/dashboard', { replace: true });
  };

  const handleLogout = () => {
    apiService.logout();
    navigate('/login', { replace: true });
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Choisir un établissement
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Compte super-admin : sélectionnez le partenaire à piloter dans ce dashboard.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          size="small"
          label="Rechercher par nom ou n°"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 360, overflow: 'auto', border: 1, borderColor: 'divider' }}>
            {filtered.map((p) => (
              <ListItemButton key={p.id} onClick={() => handleChoose(p.id)}>
                <ListItemText
                  primary={p.name}
                  secondary={[p.type, p.status].filter(Boolean).join(' · ') || `ID ${p.id}`}
                />
              </ListItemButton>
            ))}
            {filtered.length === 0 && (
              <Box px={2} py={2}>
                <Typography variant="body2" color="text.secondary">
                  Aucun résultat.
                </Typography>
              </Box>
            )}
          </List>
        )}

        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
          <Button onClick={handleLogout} color="inherit">
            Déconnexion
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default SelectProviderPage;
